import { useState } from 'preact/hooks'
import { calendarOf, text } from '#contract'
import { useView } from '../app/use-view.ts'
import { weekdayName } from '../i18n/dates.ts'
import { useLocale, useT } from '../i18n/index.ts'
import { appById, appName } from './apps/index.ts'
import { formatOsTime } from './clock.ts'
import { BatteryGlyph, LogoGlyph, SignalGlyph, WifiGlyph } from './glyphs.tsx'
import { MenuBarMenu } from './menu-bar-menu.tsx'
import type { OsMenuItem } from './menu-bar-menu.tsx'
import type { OsMenuHint } from './os.tsx'
import type { OsState } from './os-state.ts'
import styles from './status-bar.module.css'

interface StatusBarProps {
  readonly state: OsState
  readonly minutes: number
  readonly systemItems: readonly OsMenuItem[]
  readonly hint: OsMenuHint | null
}

/** Stub menus: they open, and every item is disabled until an app gives them meaning. */
const STUB_MENUS: readonly { readonly id: string; readonly items: readonly OsMenuItem[] }[] = [
  {
    id: 'file',
    items: [{ label: text('os.menu.newWindow') }, { label: text('os.menu.closeWindow') }],
  },
  {
    id: 'edit',
    items: [
      { label: text('os.menu.undo') },
      { label: text('os.menu.copy') },
      { label: text('os.menu.paste') },
    ],
  },
  {
    id: 'view',
    items: [{ label: text('os.menu.showSidebar') }, { label: text('os.menu.enterFullScreen') }],
  },
]

/**
 * The top of the screen. The phone: the time and the signal, Wi-Fi and battery either side
 * of the Dynamic Island. The desktop: the menu bar (the system menu, the front app's name
 * and menus, the host's hint, the weekday and the clock).
 */
export function StatusBar(props: StatusBarProps) {
  return props.state.form === 'phone' ? <PhoneStatus {...props} /> : <MenuBar {...props} />
}

function PhoneStatus({ state, minutes }: StatusBarProps) {
  const front = state.front.value
  // White over the wallpaper and dark app headers, the text colour over the rest.
  const light = front === null || appById(front).lightStatus === true
  return (
    <header class={styles.phone} data-light={light || undefined}>
      <span class={styles.time}>{formatOsTime(minutes)}</span>
      <span class={styles.glyphs} aria-hidden="true">
        <SignalGlyph class={styles.signal} />
        <WifiGlyph class={styles.wifi} />
        <BatteryGlyph class={styles.battery} />
      </span>
    </header>
  )
}

function MenuBar({ state, minutes, systemItems, hint }: StatusBarProps) {
  const t = useT()
  const locale = useLocale()
  const day = useView((view) => view.os.day)
  const [open, setOpen] = useState<string | null>(null)
  const front = state.front.value
  const time = formatOsTime(minutes)
  // "Mon 18:20", as macOS shows it; the device clock alone without the game's.
  const clock =
    day === null
      ? time
      : t(text('os.menuBar.clock', { weekday: weekdayName(locale, calendarOf(day).weekday), time }))
  return (
    <header class={[styles.menuBar, 'os-glass'].join(' ')}>
      <MenuBarMenu
        id="system"
        name={t(text('os.system'))}
        items={systemItems}
        open={open}
        onOpen={setOpen}
      >
        <LogoGlyph class={styles.logo} />
      </MenuBarMenu>
      <span class={styles.appName}>{t(front === null ? text('os.desktop') : appName(front))}</span>
      {STUB_MENUS.map((menu) => (
        <MenuBarMenu key={menu.id} id={menu.id} items={menu.items} open={open} onOpen={setOpen}>
          {t(text(`os.menu.${menu.id}`))}
        </MenuBarMenu>
      ))}
      <span class={styles.spacer} />
      {hint && (
        <span class={styles.hint}>
          <kbd class={styles.key}>{hint.key}</kbd>
          {t(hint.label)}
        </span>
      )}
      <span class={styles.glyphs} aria-hidden="true">
        <WifiGlyph class={styles.wifi} />
        <BatteryGlyph class={styles.battery} />
      </span>
      <span class={styles.clock}>{clock}</span>
    </header>
  )
}
